import type { CommunitySwitcher } from "@circle-react/types/CommunitySwitcher";
import { getAutomaticLoginRootUrl } from "@circleco/community-switcher/helpers/utils";
import { Drawer } from "@circleco/compass/components/Drawer";
import { Icon } from "@circleco/compass/components/Icon";
import { Typography } from "@circleco/compass/components/Typography";

import { CommunityAvatar } from "./CommunityAvatar";

interface CommunityRowProps {
  community: CommunitySwitcher;
  isCurrent?: boolean;
  isDarkAppearance: boolean;
  token: string;
}

/**
 * A single community entry in the mobile community switcher. The current
 * community renders as a static highlighted row with a checkmark; other
 * communities render as a link that navigates via automatic-login URL.
 */
export const CommunityRow = ({
  community,
  isDarkAppearance,
  token,
  isCurrent = false,
}: CommunityRowProps) => {
  const href = getAutomaticLoginRootUrl(community.root_url, token);

  // Extract domain from root_url for subtitle display
  let domain = "";
  try {
    domain = new URL(community.root_url).hostname;
  } catch {
    // fall through
  }

  const content = (
    <div className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5">
      <CommunityAvatar
        community={community}
        isDarkAppearance={isDarkAppearance}
        size={isCurrent ? "md" : "sm"}
      />
      <div className="flex min-w-0 flex-1 flex-col">
        <Typography variant={isCurrent ? "label-lg" : "body-md"}>
          {community.name}
        </Typography>
        {domain && (
          <Typography variant="label-sm" color="tertiary">
            {domain}
          </Typography>
        )}
      </div>
      {isCurrent && <Icon name="checkmark" size="sm" />}
    </div>
  );

  if (isCurrent) {
    return content;
  }

  return (
    <Drawer.Close nativeButton={false}>
      <a
        href={href}
        className="block rounded-lg hover:bg-hover"
        data-community-switcher-nav=""
      >
        {content}
      </a>
    </Drawer.Close>
  );
};
